import { isNumber } from 'effect/Number'
import ConsumableAttempt from 'features/common/ConsumableAttempt/domain/ConsumableAttempt'
import { Skiptrace as Domain } from 'features/Skiptrace/domain/Skiptrace.domain'
import { mapPerformedSkiptraceResponse } from 'features/Skiptrace/infra/remote/performSkiptrace/performSkiptrace'
import { Pennies } from 'features/valueObjects/Pennies'
import { ErrorLib } from 'libs/errors'
import { apolloClient, restClient } from 'presentation/libs/client'
import { GET_SKIPTRACE_FUNDS_INFO_GQL } from './getSkiptraceFundsInfo.graphql'

type SkiptraceFeature = {
  __typename: 'SubscriptionFeatureSkiptrace'
  prices?: { price?: number | null }[]
}

type IndividualSkiptraceFeature = {
  __typename: 'SubscriptionFeatureIndividualSkiptrace'
  limits?: { interval?: string | null, value?: number | null }[]
  prices?: { price?: number | null }[]
}

type SkiptraceBillingInfo = {
  planName: string
  walletBalance: number
  consumablesRefreshDate: Date
  price: number | null
  hasUnlimitedIndividualSkiptrace: boolean
}

/**
 * Determine the status based on remaining consumables, available funds, and how many records need to be skiptrace'd
 */
const determineStatus = ({
  remainingConsumables,
  availableFunds,
  toSkiptraceCount,
  pricePerUnit,
}: {
  remainingConsumables: number
  availableFunds: number
  toSkiptraceCount: number
  pricePerUnit: number
}): ConsumableAttempt.Status => {
  if (remainingConsumables >= toSkiptraceCount)
    return 'sufficient-consumables'
  else if (availableFunds >= pricePerUnit * (toSkiptraceCount - remainingConsumables))
    return 'sufficient-funds'
  else
    return 'insufficient-funds'
}

const getSkiptraceBillingInfo = async (): Promise<SkiptraceBillingInfo> => {
  const { data } = await apolloClient.query({
    query: GET_SKIPTRACE_FUNDS_INFO_GQL,
  })

  const enterprise = data.myEnterprise

  if (!enterprise)
    throw new Error('undefined `myEnterprise` in GraphQL response')

  const subscription = enterprise.subscription

  if (subscription?.__typename !== 'BillingSubscriptionActive')
    throw new Error('undefined or inactive subscription')

  const skiptraceFeature = subscription.plan.product.features
    .find((feat: any): feat is SkiptraceFeature =>
      feat.__typename === 'SubscriptionFeatureSkiptrace')

  const individualSkiptraceFeature = subscription.plan.product.features
    .find((feat: any): feat is IndividualSkiptraceFeature =>
      feat.__typename === 'SubscriptionFeatureIndividualSkiptrace')

  return {
    planName: subscription.plan.name,
    walletBalance: enterprise.wallet?.balance?.available ?? 0,
    consumablesRefreshDate: subscription.currentPeriod?.end
      ? new Date(subscription.currentPeriod.end)
      : new Date(0),
    price: individualSkiptraceFeature?.prices?.[0]?.price
    ?? skiptraceFeature?.prices?.[0]?.price
    ?? null,
    hasUnlimitedIndividualSkiptrace: !!individualSkiptraceFeature
    && (individualSkiptraceFeature.limits?.length ?? 0) === 0,
  }
}

const createUnlimitedIndividualAttempt = (
  billingInfo: SkiptraceBillingInfo,
): Domain.GetSkiptraceFundsInfoResult => ({
  attempt: {
    planName: billingInfo.planName,
    price: 0,
    refreshingConsumables: 0,
    remainingConsumables: 1,
    consumablesRefreshDate: billingInfo.consumablesRefreshDate,
    funds: {
      available: billingInfo.walletBalance as Pennies,
    },
    confirmationKey: '',
    status: 'sufficient-consumables',
  },
})

const createFallbackUnlimitedAttempt = (): Domain.GetSkiptraceFundsInfoResult => ({
  attempt: {
    planName: '',
    price: 0,
    refreshingConsumables: 0,
    remainingConsumables: 1,
    consumablesRefreshDate: new Date(0),
    funds: {
      available: 0 as Pennies,
    },
    confirmationKey: '',
    status: 'sufficient-consumables',
  },
})

export const getSkiptraceFundsInfo = async (
  params: Domain.GetSkiptraceFundsInfoPayload,
): Promise<Domain.GetSkiptraceFundsInfoResult> => {
  const billingInfo = await getSkiptraceBillingInfo()
    .catch(error => {
      void error
      return null
    })

  if (billingInfo?.hasUnlimitedIndividualSkiptrace)
    return createUnlimitedIndividualAttempt(billingInfo)

  const commonPayload = {
    type: 'basic',
    request: {
      line1: params.address.line1,
      city: params.address.city,
      state: params.address.state,
      zip: params.address.postalCode,
    },
    useIncludedConsumables: true,
  }

  // When the user does not have unlimited individual skiptraces, probe the
  // wallet path and use the payment confirmation response as the billing-state
  // source of truth.
  return restClient.post('marketing/v2/skiptrace/v4', {
    json: commonPayload,
  })
    .json<any>()
    .then(async response => {
      const performed = await mapPerformedSkiptraceResponse(response, params)

      return {
        ...billingInfo
          ? createUnlimitedIndividualAttempt(billingInfo)
          : createFallbackUnlimitedAttempt(),
        result: performed.result,
      }
    })
    .catch(async error => {
      // Check if it's an error with a response
      if (error?.response) {
        try {
          const errorResponse = await error.response.json()

          // Check if it's the payment confirmation required error
          if (errorResponse?.error?.type === 'payment_confirmation_required') {
            // Extract data from the error response first
            const confirmationKey = errorResponse.error.confirmationKey

            // Check if we have consumable data in the error response
            const restRemainingConsumables = errorResponse.error?.details?.included?.available
            const restRefreshingConsumables = errorResponse.error?.details?.included?.limit
            const restRefreshDate = errorResponse.error?.details?.included?.rolloverDate
            const restWalletBalance = errorResponse.error?.details?.balance?.available
            const restPrice = errorResponse.error?.details?.unitPrice
            const planName = errorResponse.error?.details?.planName
              ?? billingInfo?.planName
              ?? ''
            const price = restPrice ?? billingInfo?.price

            if (!isNumber(price))
              throw new Error('missing Skiptrace price data')

            const remainingConsumables = restRemainingConsumables ?? 0
            const refreshingConsumables = restRefreshingConsumables ?? 0
            const consumablesRefreshDate = restRefreshDate
              ? new Date(restRefreshDate)
              : billingInfo?.consumablesRefreshDate ?? new Date()
            const walletBalance = restWalletBalance
              ?? billingInfo?.walletBalance
              ?? 0

            // Create the response object
            return {
              attempt: {
                planName,
                price,
                refreshingConsumables,
                remainingConsumables,
                consumablesRefreshDate,
                funds: {
                  available: walletBalance as Pennies,
                },
                confirmationKey,
                status: determineStatus({
                  remainingConsumables,
                  availableFunds: walletBalance,
                  toSkiptraceCount: 1, // For single skiptrace, we're only processing one record
                  pricePerUnit: price,
                }),
              },
            }
          }
        } catch (parseError) {
          // Error parsing the response JSON
          ErrorLib.report(parseError, {
            extraInfo: { originalError: error, context: 'Error parsing skiptrace funds info response' },
          })

          throw parseError
        }
      }

      // If we reached here, it's not the expected error format or failed to parse
      const extraInfo = await ErrorLib.safeParseError(error)
      ErrorLib.report(error, {
        extraInfo: { ...extraInfo, context: 'Skiptrace funds info attempt failed' },
      })

      throw error
    })
}
